/**
 * Real-Time Context
 * 
 * Unified context for real-time communication using SignalR (.NET backend)
 * Replaces SocketContext which was for the deprecated Node.js backend
 */

import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import { createAuthHeaders } from '@/utils/authHeaders';
import { useIdentity } from '@/hooks/useIdentity';
import { useSubscription } from './SubscriptionContext';
import { getBackendUrl } from '@/utils/environment';

interface RealTimeContextType {
  connection: signalR.HubConnection | null;
  isConnected: boolean;
  activeExecutionId: string | null;
  setActiveExecution: (executionId: string | null) => void;
  subscribeToExecution: (executionId: string) => void;
  unsubscribeFromExecution: (executionId: string) => void;
}

const RealTimeContext = createContext<RealTimeContextType | undefined>(undefined);

export function RealTimeProvider({ children }: { children: React.ReactNode }) {
  const [isConnected, setIsConnected] = useState(false);
  const [activeExecutionId, setActiveExecutionIdState] = useState<string | null>(null);
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const isInitializedRef = useRef(false);
  const activeExecutionIdRef = useRef<string | null>(null);
  const { getAccessToken, isAuthenticated } = useIdentity();
  const { subscriptionId } = useSubscription();

  // Keep ref in sync with state for use in callbacks
  useEffect(() => {
    activeExecutionIdRef.current = activeExecutionId;
  }, [activeExecutionId]);

  const setActiveExecution = useCallback((executionId: string | null) => {
    setActiveExecutionIdState(executionId);
    
    // Don't automatically request state recovery here
    // State recovery should only happen on reconnection (handled in onreconnected)
    // Subscribing to an execution doesn't need immediate state recovery
  }, []);

  const subscribeToExecution = useCallback((executionId: string) => {
    if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
      connectionRef.current.invoke('SubscribeToExecution', executionId)
        .catch(err => console.error('[SignalR] ❌ Error subscribing to execution:', err));
    }
  }, []);

  const unsubscribeFromExecution = useCallback((executionId: string) => {
    if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
      connectionRef.current.invoke('UnsubscribeFromExecution', executionId)
        .catch(err => console.error('[SignalR] ❌ Error unsubscribing from execution:', err));
    }
  }, []);

  const registerEventListeners = (connection: signalR.HubConnection) => {
    // console.log('[SignalR] 📝 Registering event listeners...');
    
    // Remove all existing listeners first to prevent duplicates
    connection.off('SubscribeExecutionSuccess');
    connection.off('SubscribeWorkflowSuccess');
    connection.off('execution:started');
    connection.off('execution:completed');
    connection.off('execution:failed');
    connection.off('execution:cancelled');
    connection.off('execution:state:recovered');
    connection.off('ExecutionNotFound');
    connection.off('ExecutionRecoveryFailed');
    connection.off('node:started');
    connection.off('node:completed');
    connection.off('node:failed');
    connection.off('loop:started');
    connection.off('loop:completed');
    connection.off('loop:iteration');
    connection.off('loop:iteration:started');
    connection.off('loop:iteration:completed');
    connection.off('subnode:started');
    connection.off('subnode:completed');
    connection.off('subnode:failed');
    
    // Subscription confirmation events
    connection.on('SubscribeExecutionSuccess', (data: any) => {
      // Successfully subscribed
    });
    
    connection.on('SubscribeWorkflowSuccess', (data: any) => {
      // Successfully subscribed
    });
    
    // Execution events (using colon-separated names from backend)
    connection.on('execution:started', (data: any) => {
      window.dispatchEvent(new CustomEvent('execution:started', { detail: data }));
    });

    connection.on('execution:completed', (data: any) => {
      window.dispatchEvent(new CustomEvent('execution:completed', { detail: data }));
    });

    connection.on('execution:failed', (data: any) => {
      window.dispatchEvent(new CustomEvent('execution:failed', { detail: data }));
    });

    connection.on('execution:cancelled', (data: any) => {
      window.dispatchEvent(new CustomEvent('execution:cancelled', { detail: data }));
    });

    // State recovery event (sent by RecoverExecutionState hub method)
    connection.on('execution:state:recovered', (data: any) => {
      window.dispatchEvent(new CustomEvent('execution-state-recovered', { detail: data }));
    });

    // State recovery error events
    connection.on('ExecutionNotFound', (data: any) => {
      window.dispatchEvent(new CustomEvent('execution-not-found', { detail: data }));
    });

    connection.on('ExecutionRecoveryFailed', (data: any) => {
      window.dispatchEvent(new CustomEvent('execution-recovery-failed', { detail: data }));
    });

    // Node events
    connection.on('node:started', (data: any) => {
      window.dispatchEvent(new CustomEvent('node:started', { detail: data }));
    });

    connection.on('node:completed', (data: any) => {
      window.dispatchEvent(new CustomEvent('node:completed', { detail: data }));
    });

    connection.on('node:failed', (data: any) => {
      window.dispatchEvent(new CustomEvent('node:failed', { detail: data }));
    });

    // Loop events - transform to node events with real nodeExecutionId from backend
    connection.on('loop:started', (data: any) => {
      // Transform to node:started event with nodeExecutionId from backend
      const nodeEvent = {
        executionId: data.executionId,
        nodeExecutionId: data.nodeExecutionId, // Real ID from backend
        nodeId: data.loopNodeId,
        nodeName: 'Loop',
        nodeType: 'loop',
        startedAt: data.startedAt,
        status: 'running',
        metadata: {
          totalItems: data.totalItems,
          isLoopContainer: true
        }
      };
      window.dispatchEvent(new CustomEvent('node:started', { detail: nodeEvent }));
    });

    connection.on('loop:completed', (data: any) => {
      // Transform to node:completed event with nodeExecutionId from backend
      const nodeEvent = {
        executionId: data.executionId,
        nodeExecutionId: data.nodeExecutionId, // Real ID from backend
        nodeId: data.loopNodeId,
        nodeName: 'Loop',
        nodeType: 'loop',
        completedAt: data.completedAt,
        status: 'success',
        metadata: {
          isLoopContainer: true
        }
      };
      window.dispatchEvent(new CustomEvent('node:completed', { detail: nodeEvent }));
    });

    connection.on('loop:iteration', (data: any) => {
      window.dispatchEvent(new CustomEvent('loop:iteration', { detail: data }));
    });

    connection.on('loop:iteration:started', (data: any) => {
      window.dispatchEvent(new CustomEvent('loop:iteration:started', { detail: data }));
    });

    connection.on('loop:iteration:completed', (data: any) => {
      window.dispatchEvent(new CustomEvent('loop:iteration:completed', { detail: data }));
    });

    // Sub-node events (tool executions within AI agents)
    connection.on('subnode:started', (data: any) => {
      window.dispatchEvent(new CustomEvent('subnode:started', { detail: data }));
    });

    connection.on('subnode:completed', (data: any) => {
      window.dispatchEvent(new CustomEvent('subnode:completed', { detail: data }));
    });

    connection.on('subnode:failed', (data: any) => {
      window.dispatchEvent(new CustomEvent('subnode:failed', { detail: data }));
    });

    // console.log('[SignalR] ✅ Event listeners registered');
  };

  useEffect(() => {
    // Only initialize when authenticated
    if (!isAuthenticated) {
      return;
    }

    // Wait for subscriptionId before connecting (critical for multi-tenancy)
    // In Native Tab mode, subscriptionId comes from instanceId (async from parent frame)
    // In Standalone mode, subscriptionId comes from API call (async fetch)
    // E2B mode doesn't use subscriptionId, so it will be null
    if (!subscriptionId) {
      console.log('[SignalR] 📋 Waiting for subscriptionId before connecting...');
      return;
    }

    // Prevent double initialization in React Strict Mode
    if (isInitializedRef.current) {
      // console.log('[SignalR] Already initialized, skipping');
      return;
    }
    
    // If already connected, re-register event listeners to pick up code changes
    if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
      // console.log('[SignalR] Already connected, re-registering event listeners');
      registerEventListeners(connectionRef.current);
      return;
    }
    
    if (connectionRef.current?.state === signalR.HubConnectionState.Connecting) {
      // console.log('[SignalR] Already connecting, skipping');
      return;
    }
    
    isInitializedRef.current = true;
    
    // Initialize SignalR connection
    const initConnection = async () => {
      try {
        // Get fresh token from identity provider
        const token = await getAccessToken();
        
        // SignalR hub URL - ExecutionHub endpoint with subscriptionId query parameter
        const hubUrl = subscriptionId
          ? `${getBackendUrl()}/hubs/execution?subscriptionId=${subscriptionId}`
          : `${getBackendUrl()}/hubs/execution`;

        console.log('[SignalR] 🔌 Initializing connection to:', hubUrl, {
          subscriptionId,
          hasSubscriptionId: !!subscriptionId
        });
        
        // Build SignalR connection
        const connection = new signalR.HubConnectionBuilder()
          .withUrl(hubUrl, {
            accessTokenFactory: () => token || '',
            transport: signalR.HttpTransportType.WebSockets | 
                       signalR.HttpTransportType.ServerSentEvents | 
                       signalR.HttpTransportType.LongPolling,
            skipNegotiation: false
          })
          .withAutomaticReconnect({
            nextRetryDelayInMilliseconds: (retryContext) => {
              // Exponential backoff: 1s, 2s, 4s, 8s, 16s, max 30s
              const delay = Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000);
              console.log(`[SignalR] 🔄 Reconnection attempt ${retryContext.previousRetryCount + 1}, waiting ${delay}ms`);
              return delay;
            }
          })
          .configureLogging(signalR.LogLevel.Information)
          .build();

        connectionRef.current = connection;

        // Connection event handlers
        connection.onclose((error) => {
          console.log('[SignalR] ⚠️ Connection closed', error ? `Error: ${error.message}` : '');
          setIsConnected(false);
        });

        connection.onreconnecting((error) => {
          console.log('[SignalR] 🔄 Reconnecting...', error ? `Error: ${error.message}` : '');
          setIsConnected(false);
        });

        connection.onreconnected((connectionId) => {
          console.log('[SignalR] ✅ Reconnected successfully', { connectionId });
          setIsConnected(true);

          // Request state recovery for active execution on reconnect
          // Use ref to get current value without adding to useEffect deps
          if (activeExecutionIdRef.current) {
            connection.invoke('RecoverExecutionState', activeExecutionIdRef.current)
              .catch(err => console.error('[SignalR] Error requesting state recovery:', err));
          }
        });

        // Register event listeners BEFORE starting connection
        registerEventListeners(connection);
        
        // Start connection
        await connection.start();
        
        setIsConnected(true);
        
      } catch (error) {
        console.error('[SignalR] ❌ Failed to connect:', error);
        setIsConnected(false);
      }
    };

    let isMounted = true;

    const connectAsync = async () => {
      if (isMounted) {
        await initConnection();
      }
    };

    connectAsync();

    // Cleanup on unmount
    return () => {
      isMounted = false;
      if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
        // console.log('[SignalR] 🔌 Disconnecting...');
        connectionRef.current.stop()
          .then(() => {
            console.log('[SignalR] Disconnected');
            isInitializedRef.current = false;
          })
          .catch(err => {
            console.error('[SignalR] Error disconnecting:', err);
            isInitializedRef.current = false;
          });
      } else {
        // If not connected, just reset the flag
        isInitializedRef.current = false;
      }
    };
  }, [isAuthenticated, getAccessToken, subscriptionId]);

  const value: RealTimeContextType = {
    connection: connectionRef.current,
    isConnected,
    activeExecutionId,
    setActiveExecution,
    subscribeToExecution,
    unsubscribeFromExecution,
  };

  return (
    <RealTimeContext.Provider value={value}>
      {children}
    </RealTimeContext.Provider>
  );
}

export function useRealTime() {
  const context = useContext(RealTimeContext);
  if (context === undefined) {
    throw new Error('useRealTime must be used within a RealTimeProvider');
  }
  return context;
}

// Backward compatibility: export as useSocket for existing code
export const useSocket = useRealTime;
